// Cálculo de progresso da jornada. `done` = tarefas com status 'done'.
// `percent` = arredondado; 0 quando não há tarefas (evita divisão por zero).
import { NIVEIS, NIVEL_DEFAULT } from '@modules/central-config/constants/Text'

const STATUS_DONE = 'done'
const TIER_OPCIONAL = 'opcional'

// Níveis canônicos (1→4) derivados de NIVEIS — nunca duplicar o range aqui: se o
// contrato ganhar/perder um nível, este util acompanha sozinho. `sort` age na
// cópia criada pelo `map` (NIVEIS não é mutado) e garante a ordem documentada
// mesmo que a constante seja reordenada.
const NIVEIS_ORDENADOS = NIVEIS.map((item) => item.nivel).sort((a, b) => a - b)

function isDone(task) {
  return !!(task && task.metadata && task.metadata.status === STATUS_DONE)
}

// spec-002 — progresso SÓ-OBRIGATÓRIA (metadata.tier).
// Uma etapa é essencial quando NÃO é 'opcional': tier ausente ou 'obrigatorio'
// contam como essencial (fail-open — espelha o backend e garante não-regressão
// para escritório sem perfil, onde nenhuma etapa vem classificada).
//
// ★ Vitrine nunca é essencial (2026-08-05). Ela é obrigatória no catálogo — para
// quem TEM a flag ela é trabalho de verdade —, mas quem não tem não consegue
// concluí-la: mantida no denominador, o anel do módulo jamais fecharia. Sair
// daqui resolve os dois lados de uma vez, porque este é o único predicado que
// `moduleEssentialProgress`, `progressoPorNivel`, `proximaEtapaPendenteDoModulo`
// e o agrupamento do accordion consultam — e é o que também a joga no bloco
// "Quando quiser ir além", colapsado por padrão, que é onde ela deve ficar.
export function isEssential(task) {
  if (!task || !task.metadata) return true
  if (task.metadata.vitrine) return false
  return task.metadata.tier !== TIER_OPCIONAL
}

// Carimba uma chave booleana no `metadata` das etapas que o predicado aponta.
//
// Cópia rasa em cada nível (módulo → task → metadata): o alvo é o array VIVO do
// Vuex, e mutar `metadata` no lugar gravaria a marca no store — onde ela
// sobreviveria a uma troca de escritório com outras flags. Módulo sem nenhuma
// etapa marcada volta pela MESMA referência, para não invalidar computeds à toa.
export function marcarEtapas(modules, predicado, chave) {
  const list = Array.isArray(modules) ? modules : []
  if (typeof predicado !== 'function' || !chave) return list
  return list.map((module) => {
    const tasks = (module && Array.isArray(module.tasks) && module.tasks) || []
    if (!tasks.some(predicado)) return module
    return {
      ...module,
      tasks: tasks.map((task) =>
        predicado(task)
          ? { ...task, metadata: { ...(task.metadata || {}), [chave]: true } }
          : task
      ),
    }
  })
}

// A etapa que o escritório vê mas não pode fazer por não ter o recurso.
export function marcarVitrines(modules, ehVitrine) {
  return marcarEtapas(modules, ehVitrine, 'vitrine')
}

// ★ A etapa OBRIGATÓRIA cuja tela o escritório não contratou (2026-08-05, review
// do PR #1854 — achado CRITICAL).
//
// Ela NÃO pode simplesmente sumir, e não é questão de estética: o auto-avanço de
// nível é do backend, que exige `done >= total` sobre TODAS as obrigatórias e não
// conhece contrato nenhum. Escondendo a etapa, o front passa a mostrar 100% e
// "Pronto para usar" enquanto o backend nunca promove — e some junto a única
// saída que existia, o dot clicável. Um escritório com Consolidador e sem Asset
// Allocation (combinação comum: 5 obrigatórias em /configuracoes/asset-allocation)
// ficaria preso no Nível 1 para sempre, sem nada na tela explicando por quê.
//
// Marcada em vez de removida, ela continua no denominador — front e backend
// contam o mesmo —, aparece num grupo COLAPSADO ("ir além"), que era o problema
// original que motivou esconder, e mantém o dot para destravar o nível.
//
// A OPCIONAL barrada continua sumindo (`filtrarModulosVisiveis`): ela não entra
// no denominador, então não trava nada, e mostrá-la seria só ruído.
export function marcarIndisponiveis(modules, ehIndisponivel) {
  return marcarEtapas(modules, ehIndisponivel, 'indisponivel')
}

// Ordem VISUAL das obrigatórias de um módulo — as obrigatórias de
// `module.tasks`, preservando a ordem em que chegam.
//
// ★ Por que basta filtrar, sem reordenar (2026-08-09): a trilha (agrupamento +
// ordem) migrou para o backend — `config_journey_catalog.py` já entrega
// `module.tasks` na sequência visual final (trilha 1 → trilha 2 → ... → sem
// trilha), a mesma que `agruparPorTrilha` (utils/module-sections.js) usa para
// desenhar as seções do accordion. Antes esta função precisava RECONSTRUIR essa
// ordem lendo `constants/trilhas.js` (dívida consciente do front, removida
// nesta migração) porque o catálogo entregava as etapas na ordem do
// drag-and-drop do Interno, que podia divergir da trilha — "Conectar e-mail" no
// CRM chegava ~10ª no array cru mas era a 1ª da trilha "Ferramentas", e o
// drawer mostrava "10 de 21" sobre a primeira linha visual. Preservar a ordem
// de entrada agora é suficiente porque a ordem de entrada JÁ é a visual.
export function essentialTasksNaOrdemVisual(module) {
  const tasks = (module && Array.isArray(module.tasks) && module.tasks) || []
  return tasks.filter(isEssential)
}

// Ordem VISUAL COMPLETA das etapas de um módulo — a sequência de cima para
// baixo que o accordion mostra: obrigatórias (`essentialTasksNaOrdemVisual`) e
// as opcionais logo depois, na ordem em que chegam (o divisor "Quando quiser ir
// além" fica entre as duas).
//
// ★ Por que existe: dirige a navegação prev/next do drawer, que é GLOBAL (cruza
// a fronteira obrigatória/opcional) — precisa de uma lista única, não dos pares
// por grupo que `essentialTasksNaOrdemVisual` resolve sozinho.
export function tasksNaOrdemVisual(module) {
  const tasks = (module && Array.isArray(module.tasks) && module.tasks) || []
  return essentialTasksNaOrdemVisual(module).concat(
    tasks.filter((task) => !isEssential(task))
  )
}

// Progresso só-obrigatória de um módulo → { done, total, percent }.
// 0 obrigatórias ⇒ percent 100 (trivialmente pronto), nunca NaN.
export function moduleEssentialProgress(module) {
  const tasks = (module && Array.isArray(module.tasks) && module.tasks) || []
  const essential = tasks.filter(isEssential)
  const total = essential.length
  const done = essential.filter(isDone).length
  const percent = total ? Math.round((done / total) * 100) : 100
  return { done, total, percent }
}

// Módulo "pronto para usar" = todas as obrigatórias concluídas
// (0 obrigatórias ⇒ true, selo imediato).
export function isModuleReady(module) {
  return moduleEssentialProgress(module).percent === 100
}

// spec-004 — NÍVEL (metadata.nivel). Dimensão ORTOGONAL ao tier: o nível decide
// QUANDO um módulo/etapa entra na jornada; o tier decide, DENTRO daquele nível,
// o que é obrigatório. Fail-open igual ao do tier: item sem classificação ⇒
// nível 1 (aparece desde o começo) — a gamificação nunca pode esconder etapa.
// Aceita string numérica ("2") porque rebaixar um "2" para 1 mudaria o módulo de
// seção silenciosamente; valor fora do catálogo (0, 5, 2.5, null) cai no default.
// Obs.: a validação aqui é por PERTENCIMENTO a NIVEIS_ORDENADOS, não por
// checagem de inteiro — `Number.isInteger(2.5)` já seria `false`, mas também
// aceitaria o 5 e o 0, que estão fora do catálogo. (Este comentário dizia que
// `Number.isInteger` estava sobrescrito e quebrado globalmente; era verdade até
// `32ceee79b`, que removeu o override de `src/utils/InitFunctions.js` — o
// nativo funciona desde então, e o motivo da checagem por catálogo é o acima.)
export function normalizeNivel(value) {
  const nivel = Number(value)
  return NIVEIS_ORDENADOS.indexOf(nivel) >= 0 ? nivel : NIVEL_DEFAULT
}

// Nível de uma ETAPA (1-4), mesmo fail-open de `moduleNivel`. Interna: usada
// por `groupModulesByNivel`, `proximaEtapaObrigatoria` e
// `nivelOndeEtapaAparece` — quem pertence a um nível é a etapa, então este é o
// discriminador real do agrupamento.
function taskNivelDe(task) {
  const metadata = (task && task.metadata) || {}
  return normalizeNivel(metadata.nivel)
}

// Nível de um MÓDULO → Number (1-4). Defensivo: module/metadata ausentes ⇒ default.
//
// ★ Atenção ao papel: quem pertence a um nível é a ETAPA — o agrupamento da
// listagem é por `taskNivelDe` (ver `groupModulesByNivel` e
// `proximaEtapaObrigatoria`), e por isso um mesmo módulo pode aparecer em
// vários níveis. `moduleNivel` sobrevive só como (a) rótulo informativo do card
// e (b) fallback do módulo SEM nenhuma etapa, que precisa cair em algum nível
// para não sumir da Jornada. Não use para decidir em que seção um card está —
// nesse caso o certo é `nivelOndeModuloAparece`, que deriva da mesma
// distribuição da listagem em vez de reimplementar a regra.
export function moduleNivel(module) {
  const metadata = (module && module.metadata) || {}
  return normalizeNivel(metadata.nivel)
}

// Agrupa por nível olhando o nível de cada ETAPA, não o do módulo.
//
// ★ Regra de negócio (confirmada 2026-07-29): "se uma etapa do Consolidador tem
// nível Básico mas o Consolidador é Especialista, na tela do Básico
// deve aparecer o módulo Consolidador com APENAS aquela etapa embaixo dele".
// Ou seja: o módulo é apenas o agrupador visual — quem pertence a um nível é a
// ETAPA. Um mesmo módulo pode aparecer em vários níveis, cada vez com o
// subconjunto de etapas daquele nível.
//
// Vale para obrigatórias E opcionais: uma opcional de nível 3 só aparece no
// nível 3. O nível do MÓDULO (`moduleNivel`) segue existindo só como rótulo
// informativo no card.
//
// Devolve SEMPRE os 4 níveis, em ordem 1→4, mesmo vazios — esconder seção vazia
// é decisão do consumidor (a view), e manter os 4 estabiliza ordem/`:key`.
export function groupModulesByNivel(modules) {
  const list = Array.isArray(modules) ? modules : []
  const groups = NIVEIS_ORDENADOS.map((nivel) => ({ nivel, modules: [] }))
  const byNivel = {}
  groups.forEach((group) => {
    byNivel[group.nivel] = group
  })

  list.forEach((module) => {
    const tasks = (module && Array.isArray(module.tasks) && module.tasks) || []
    // Módulo sem etapa nenhuma ainda precisa aparecer em algum lugar: cai no
    // nível declarado dele, senão sumiria da Jornada inteira.
    if (!tasks.length) {
      byNivel[moduleNivel(module)].modules.push({ ...module, tasks: [] })
      return
    }
    NIVEIS_ORDENADOS.forEach((nivel) => {
      const doNivel = tasks.filter((task) => taskNivelDe(task) === nivel)
      if (!doNivel.length) return
      // Cópia rasa com as tasks FILTRADAS — nunca mutar o módulo do store.
      byNivel[nivel].modules.push({ ...module, tasks: doNivel })
    })
  })

  return groups
}

// Nível da seção ONDE O CARD de um módulo realmente renderiza → Number ou null.
// Deriva de `groupModulesByNivel` de propósito: é a MESMA distribuição que a
// listagem usa, então o alvo é correto por construção — ler `moduleNivel` aqui
// erraria todo módulo cross-nível (declarado 2, etapas no 3: o card sai na
// seção 3, e exibir a 2 abriria uma seção onde ele não existe).
//
// WHY o MENOR quando o módulo aparece em mais de um nível: a jornada é
// percorrida do nível mais baixo para o mais alto, então a primeira seção em
// que o card aparece é de onde o usuário desceu para executar a etapa — é o
// ponto que ele reconhece ao voltar. `null` quando o módulo não está na lista:
// quem chama decide o fallback (nunca inventar um nível).
export function nivelOndeModuloAparece(modules, moduleId) {
  if (!moduleId) return null
  const grupos = groupModulesByNivel(modules)
  const grupo = grupos.find((item) =>
    item.modules.some((module) => module && module.id === moduleId)
  )
  return grupo ? grupo.nivel : null
}

// Nível da seção em que uma ETAPA específica renderiza → Number ou null.
//
// Mais preciso que `nivelOndeModuloAparece` quando o módulo é cross-nível: o
// card aparece em TODAS as seções em que tem etapa, e o helper de módulo
// escolhe a MENOR — quem executou uma etapa do nível 3 num módulo que também
// tem etapa no 1 voltaria para o 1, onde a etapa recém-trabalhada nem está
// listada (a listagem filtra as tasks por nível). Usado pelo `restaurarPosicao`
// da home (§5), que cai no helper de módulo quando não há etapa de origem.
//
// Busca a etapa DENTRO do módulo (não na jornada inteira) porque o id só é
// garantido único por módulo; `null` quando módulo/etapa não existem mais no
// catálogo — quem chama decide o fallback.
export function nivelOndeEtapaAparece(modules, moduleId, taskId) {
  if (!moduleId || !taskId) return null
  const list = Array.isArray(modules) ? modules : []
  const module = list.find((item) => item && item.id === moduleId)
  const tasks = (module && Array.isArray(module.tasks) && module.tasks) || []
  const task = tasks.find((item) => item && item.id === taskId)
  return task ? taskNivelDe(task) : null
}

// Primeira etapa OBRIGATÓRIA ainda não concluída, restrita a um nível.
// → { task, module } ou null quando não há pendência naquele nível.
//
// ★ A ORDEM É A DO BACKEND — nunca reordene aqui. O pipeline do servidor
// termina em `_ordenar_por_perfil`, que aplica a ordenação feita por
// drag-and-drop na árvore do Interno; tanto `modules[]` quanto `tasks[]` já
// chegam na sequência que o curador definiu. Ordenar/priorizar no front (por
// esforço, por módulo, por nome) quebraria silenciosamente essa curadoria.
export function proximaEtapaObrigatoria(modules, nivel) {
  const list = Array.isArray(modules) ? modules : []
  for (let m = 0; m < list.length; m += 1) {
    const module = list[m]
    const tasks = (module && Array.isArray(module.tasks) && module.tasks) || []
    for (let t = 0; t < tasks.length; t += 1) {
      const task = tasks[t]
      if (!isEssential(task)) continue
      if (isDone(task)) continue
      if (taskNivelDe(task) !== nivel) continue
      return { task, module }
    }
  }
  return null
}

// Primeira etapa OBRIGATÓRIA pendente de UM módulo, por slug → { task, module } ou null.
//
// Usado pelo CTA das telas de boas-vindas: em vez de jogar quem nunca configurou
// direto na tela crua do módulo, leva à etapa em que ele parou, com o drawer-guia.
//
// Difere de `proximaEtapaObrigatoria` em dois pontos, ambos deliberados:
//
// 1. **Ignora o nível.** Lá o nível é o eixo da listagem (a Jornada mostra um por
//    vez); aqui o usuário clicou para USAR o módulo agora, e filtrar por nível o
//    deixaria sem destino mesmo havendo etapa pendente à vista.
// 2. **Escopo de um módulo**, não do catálogo — o CTA é do módulo, não da Jornada.
//
// Só obrigatórias: obrigatórias concluídas ⇒ módulo "Pronto para usar" (mesmo
// critério de `isModuleReady`), e o CTA volta a levar direto à overview. As
// opcionais seguem disponíveis na Jornada para quem quiser ir além.
//
// `estaBloqueada(task)` é opcional e injetado pelo chamador — o accordion já
// pula essas etapas via `etapaBloqueadaPorContrato`, mas o CTA das telas de
// boas-vindas não pulava, e caía em /404 quando a 1ª obrigatória do módulo
// apontava para uma tela que o guard barra (verificado no CRM: a etapa 1 vai
// para /configuracoes/crm, inacessível a quem não é gestor). O predicado entra
// por parâmetro para esta função continuar pura — resolver a rota aqui exigiria
// router e store, que é justamente o que `feature-gate.js` isola.
// Sem predicado, o comportamento é o de antes.
export function proximaEtapaPendenteDoModulo(modules, slug, estaBloqueada) {
  if (!slug) return null
  const list = Array.isArray(modules) ? modules : []
  const module = list.find((m) => m && m.slug === slug)
  if (!module) return null

  const bloqueada =
    typeof estaBloqueada === 'function' ? estaBloqueada : () => false
  const tasks = (Array.isArray(module.tasks) && module.tasks) || []
  // `t.url` é requisito, não detalhe: sem url, `startTaskNavigation` abre o guia,
  // chama `resetStuckTransition` e RETORNA SEM NAVEGAR — o usuário clica em
  // "Explorar" e continua parado na tela de boas-vindas. Etapas assim existem no
  // catálogo (ex.: `fee-fixo/liberar-modulo-fee-fixo`, spec-006 RN-13), então a
  // 1ª obrigatória pendente pode perfeitamente ser uma delas. Aqui pulamos para a
  // próxima que tenha destino; se nenhuma tiver, devolve null e o CTA cai na
  // overview — que é um destino de verdade.
  //
  // No accordion da Jornada o caso é outro e continua válido: lá a etapa sem url
  // é uma LINHA clicável que abre o guia no lugar (é esse o passo a passo dela).
  // Este filtro vale só para quem precisa de um destino de navegação.
  const task = tasks.find(
    (t) => isEssential(t) && !isDone(t) && t.url && !bloqueada(t)
  )
  return task ? { task, module } : null
}

// Remove do catálogo o que o escritório NÃO contratou (2026-08-05).
//
// ★ Reverte a decisão anterior de manter a etapa visível como vitrine (ver
// constants/Text.js). O que a reversão custou está registrado lá — leia antes de
// desfazer.
//
// `estaBloqueada(task)` é injetado pelo chamador, mesma convenção de
// `proximaEtapaPendenteDoModulo`: resolver a rota aqui exigiria router e store,
// que é o que `feature-gate.js` isola. Sem predicado, devolve a lista intacta.
//
// Módulo é descartado quando TINHA etapas e nenhuma sobrou. Módulo que já chegou
// com `tasks: []` é PRESERVADO — `groupModulesByNivel` trata esse caso de
// propósito ("módulo sem etapa ainda precisa aparecer em algum lugar"), e mudá-lo
// aqui seria regressão não pedida.
export function filtrarModulosVisiveis(modules, estaBloqueada, moduloBloqueado) {
  const list = Array.isArray(modules) ? modules : []
  if (typeof estaBloqueada !== 'function') return list
  const moduloBarrado =
    typeof moduloBloqueado === 'function' ? moduloBloqueado : () => false
  const visiveis = []
  list.forEach((module) => {
    // ★ O módulo inteiro pode estar barrado mesmo com etapa acessível (2026-08-05).
    // Rotas de landing (`/aawz-drive/bem-vindo`) têm `customPermission` que libera
    // como UPSELL — a tela abre, então o gate por etapa não bloqueia. Resultado: o
    // AAWZ Drive não contratado aparecia na Jornada com "0 de 1 obrigatórias", uma
    // trilha cujo único destino é a página de venda. A Jornada é onde se CONFIGURA o
    // que se tem, não vitrine: se a rota inicial do módulo está barrada, ele sai.
    if (moduloBarrado(module)) return
    const tasks = (module && Array.isArray(module.tasks) && module.tasks) || []
    if (!tasks.length) {
      visiveis.push(module)
      return
    }
    const restantes = tasks.filter((task) => !estaBloqueada(task))
    if (!restantes.length) return
    // Cópia rasa — nunca mutar o módulo do store.
    visiveis.push({ ...module, tasks: restantes })
  })
  return visiveis
}

// Progresso por nível calculado no FRONT, sobre os módulos visíveis.
//
// ★ Por que o front reassume esta conta (2026-08-05): escondendo o que não é
// contratado, o `progresso_por_nivel` do backend passa a contar etapas que a
// tela não mostra — o rail diria "8 de 13" acima de uma lista de 8 itens, todos
// marcados. Não é regra de negócio duplicada: é a MESMA fórmula determinística
// (só obrigatórias, agrupadas por `metadata.nivel`) aplicada a um conjunto
// menor, sobre o mesmo `metadata.status` que o backend já resolveu por sujeito.
//
// O `nivel` em si continua do backend — aquilo é estado persistido e monotônico,
// e promover é ESCRITA.
//
// ⚠️ `total: 0 ⇒ percent: 0`, o OPOSTO de `moduleEssentialProgress` (onde 0
// obrigatórias = trivialmente pronto). A semântica aqui é a do rail: nível sem
// obrigatória classificada está VAZIO, não concluído.
export function progressoPorNivel(modules) {
  const visiveis = Array.isArray(modules) ? modules : []
  const porNivel = {}
  NIVEIS_ORDENADOS.forEach((nivel) => {
    porNivel[nivel] = { nivel, done: 0, total: 0, percent: 0 }
  })
  visiveis.forEach((module) => {
    const tasks = (module && Array.isArray(module.tasks) && module.tasks) || []
    tasks.filter(isEssential).forEach((task) => {
      const alvo = porNivel[taskNivelDe(task)]
      alvo.total += 1
      if (isDone(task)) alvo.done += 1
    })
  })
  return NIVEIS_ORDENADOS.map((nivel) => {
    const item = porNivel[nivel]
    return {
      ...item,
      percent: item.total ? Math.round((item.done / item.total) * 100) : 0,
    }
  })
}

// Nível em que o usuário está NAQUELE módulo → Number (1-4).
//
// ★ Por que existe (2026-08-05): `nivel_atual` do backend é UM número por
// escritório — a jornada inteira. Mas o percurso é por módulo: dá para estar no
// nível 2 do Fee Fixo e no nível 1 do CRM, e é assim que o profissional
// individual usa a plataforma. Sem isto, o card embutido no módulo teria de
// exibir o nível global, que não descreve aquele módulo.
//
// ⚠️ Isto NÃO promove nada e NÃO contradiz "o front nunca calcula nível": a
// promoção continua sendo ESCRITA do backend, sobre a jornada inteira, e é ela
// que alimenta `nivelAtual` no store. Aqui é leitura derivada, por módulo, só
// para exibição — nada é persistido.
//
// Regra: o menor nível do módulo que ainda tem obrigatória pendente E ACIONÁVEL.
// Todas as acionáveis concluídas ⇒ o maior nível em que o módulo tem etapa (o
// usuário domina o módulo até onde ele alcança). Módulo sem obrigatória nenhuma
// ⇒ nível 1 (fail-open, mesma filosofia de `normalizeNivel`).
//
// ★ "ACIONÁVEL" é o ponto (2026-08-07, decisão do owner com o caso em tela).
// O filtro exclui `indisponivel` e `vitrine`, mas hoje `indisponivel` nunca
// chega aqui: desde a decisão de 2026-08-07, a obrigatória barrada por
// contrato SOME (`filtrarModulosVisiveis`, journey-visibility-mixin.js) em vez
// de ser marcada — `marcarIndisponiveis` não é mais chamado por ninguém. Este
// filtro é guarda INALCANÇÁVEL para esse caso hoje, mantida de propósito (o
// contrato já virou duas vezes em três dias — ver CLAUDE.md §12/§"Nível do
// MÓDULO"): se a marcação voltar, `nivelDoModulo` já sabe não travar nela sem
// precisar de outra mudança. `vitrine` já é redundante com `isEssential`
// (que a exclui antes de chegar em `pendentes`, abaixo) — mantido também por
// clareza de intenção, não por necessidade funcional hoje.
//
// A marca `indisponivel`/`vitrine`, se algum dia voltar a ser carimbada, é
// feita pelo `journey-visibility-mixin` ANTES de chegar aqui — esta função
// continua pura, lê dado, não resolve rota.
const ehAcionavel = (task) => {
  const meta = (task && task.metadata) || {}
  return !meta.indisponivel && !meta.vitrine
}

export function nivelDoModulo(module) {
  const tasks = (module && Array.isArray(module.tasks) && module.tasks) || []
  const pendentes = tasks.filter((task) => isEssential(task) && !isDone(task))
  const acionaveis = pendentes.filter(ehAcionavel)
  // Só as inalcançáveis sobraram: não há nível "a fazer" — cai no ramo de
  // módulo dominado (maior nível), em vez de travar no menor nível pendente.
  if (acionaveis.length) {
    return acionaveis.reduce(
      (menor, task) => Math.min(menor, taskNivelDe(task)),
      NIVEIS_ORDENADOS[NIVEIS_ORDENADOS.length - 1]
    )
  }
  if (!tasks.length) return NIVEL_DEFAULT
  return tasks.reduce((maior, task) => Math.max(maior, taskNivelDe(task)), NIVEL_DEFAULT)
}

// Etapas de um módulo que estão em níveis À FRENTE de `nivel`, agrupadas por
// nível em ordem crescente → `[{ nivel, tasks }]`.
//
// ★ Por que existe (2026-08-05): a Jornada lista UM nível por vez, então as
// etapas dos níveis seguintes de um módulo não aparecem em lugar nenhum até o
// escritório subir. Um módulo com 1 obrigatória no nível 1 e mais 6 etapas nos
// níveis 2-4 exibia "100% · pronto para usar" escondendo metade do que existe —
// e para o profissional individual, que percorre a jornada no próprio ritmo,
// isso é o grosso do módulo. Estas etapas passam a sair sob "Quando quiser ir
// além", discriminadas por nível.
//
// A ORDEM DENTRO DE CADA NÍVEL É A DO BACKEND (a curadoria do Interno) — nunca
// reordenar aqui. Níveis sem etapa não geram grupo.
export function gruposDeNiveisFuturos(module, nivel) {
  const tasks = (module && Array.isArray(module.tasks) && module.tasks) || []
  const base = normalizeNivel(nivel)
  return NIVEIS_ORDENADOS.filter((n) => n > base)
    .map((n) => ({ nivel: n, tasks: tasks.filter((task) => taskNivelDe(task) === n) }))
    .filter((grupo) => grupo.tasks.length > 0)
}

// Cópia rasa do módulo só com as etapas ATÉ `nivel` (inclusive) — o complemento
// de `gruposDeNiveisFuturos`. Usada por quem recebe o módulo INTEIRO e precisa
// que a timeline principal mostre só o que já está ao alcance.
// Nunca muta o módulo do store.
export function moduloAteNivel(module, nivel) {
  if (!module) return null
  const tasks = (Array.isArray(module.tasks) && module.tasks) || []
  const base = normalizeNivel(nivel)
  return { ...module, tasks: tasks.filter((task) => taskNivelDe(task) <= base) }
}

// Módulo do catálogo pelo `slug` (a chave de lookup por módulo — `id` é UUID
// opaco). Devolve null quando não encontra, nunca undefined.
export function moduloDoCatalogo(modules, slug) {
  if (!slug) return null
  const list = Array.isArray(modules) ? modules : []
  return list.find((m) => m && m.slug === slug) || null
}

// Quantas etapas o módulo tem no total (obrigatórias + opcionais).
//
// ⚠️ Isto NÃO é progresso e não vira percentual. `moduleProgress`/
// `overallProgress` — que calculavam PERCENTUAL sobre todas as etapas — foram
// removidas em 2026-07-25 porque uma opcional pendente derrubava o número do
// módulo e o usuário nunca via 100%. O problema era do percentual, não da
// contagem: aqui o número é cru e a copy o rotula ("13 no total"), ao lado do
// "N de M obrigatórias" que segue sendo a métrica de progresso.
// Não reintroduzir divisão sobre este total.
export function moduleTaskCount(module) {
  const tasks = (module && Array.isArray(module.tasks) && module.tasks) || []
  return tasks.length
}

// Progresso só-obrigatória agregado dos módulos visíveis → { done, total, percent }.
// 0 obrigatórias no total ⇒ percent 100 (sem divisão por zero).
export function overallEssentialProgress(modules) {
  const list = Array.isArray(modules) ? modules : []
  let done = 0
  let total = 0
  list.forEach((module) => {
    const p = moduleEssentialProgress(module)
    done += p.done
    total += p.total
  })
  const percent = total ? Math.round((done / total) * 100) : 100
  return { done, total, percent }
}
